/**
 * The consultant panel's starter questions (2026-10-28). Customers barely asked the consultant anything, and the panel
 * opened on fixed examples that were not about their plant. Now, when the panel opens with no conversation, it offers
 * three or four questions made from the plant's own latest brief and day briefs, in plain words: what data the plant
 * has; what drove its largest defect, by its own word, in the brief's latest month; why its latest flagged day was
 * unusual; and what to check first today. Without a brief it offers the two that need none. A tap sends the question as
 * the person's own (the conversation's starters). Everything here is read from what the app already fetches: the
 * overall brief and the day briefs' list. Pure; no dashes as punctuation (house style).
 */
import type { BriefListItem, PlantBrief } from "@/lib/api/brief";
import { defectQuestionName, type QuickAsk } from "@/lib/brief/ask";
import { formatDay, isPlantDay } from "@/lib/plant-days";

/** What data the plant has, and from when: the question customers ask first. */
export const STARTER_DATA = "What data do we have, and from when?";

/** What to check first today: the server reads "today" on the plant's clock. */
export const STARTER_TODAY = "What should we check first today?";

/** The starters without a brief: the two that need none. */
export const GENERIC_STARTERS: readonly string[] = [STARTER_DATA, STARTER_TODAY];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December",
];

/** A plant day's month in full ("October 2026"), as a question names it; null for a day that does not read. */
export function monthOfDay(day: string): string | null {
  if (!isPlantDay(day)) return null;
  return `${MONTH_NAMES[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}`;
}

/**
 * The newest day whose brief the platform flagged: its scrap rate above its control limits (signal "above"). The list
 * is newest first, as the server sends it; a day key that does not read is passed over. null for none.
 */
export function latestFlaggedDay(days: readonly Pick<BriefListItem, "period_key" | "signal">[] | null | undefined): string | null {
  for (const d of days ?? []) {
    if (d.signal === "above" && isPlantDay(d.period_key)) return d.period_key;
  }
  return null;
}

/**
 * The panel's starter questions, from the plant's own brief and its day briefs:
 *   - "What data do we have, and from when?";
 *   - "What drove <its largest defect by pieces, in its own word> in <the brief's latest month>?", or, where the brief
 *     carries no defect, "How did our scrap rate move in <that month>?";
 *   - "Why was <the latest flagged day> unusual?", where a day brief was flagged;
 *   - "What should we check first today?".
 * Three or four with a brief; the two generic ones (GENERIC_STARTERS) without one.
 */
export function plantStarters(
  brief: Pick<PlantBrief, "defects" | "windows"> | null | undefined,
  days?: readonly Pick<BriefListItem, "period_key" | "signal">[] | null,
): string[] {
  if (!brief) return [...GENERIC_STARTERS];
  const out = [STARTER_DATA];
  const month = monthOfDay(brief.windows?.lots?.to ?? "");
  const top = [...(brief.defects ?? [])].sort((a, b) => b.pieces - a.pieces)[0];
  if (month) {
    out.push(top ? `What drove ${defectQuestionName(top)} in ${month}?` : `How did our scrap rate move in ${month}?`);
  }
  const flagged = latestFlaggedDay(days);
  if (flagged) out.push(`Why was ${formatDay(flagged)} unusual?`);
  out.push(STARTER_TODAY);
  return out;
}

/** The quick questions to show beside the starters: one a starter already asks, word for word, is left out. */
export function quickAsksBeside(asks: readonly QuickAsk[], starters: readonly string[]): QuickAsk[] {
  const asked = new Set(starters.map((s) => s.trim().toLowerCase()));
  return asks.filter((a) => !asked.has(a.text.trim().toLowerCase()));
}
